// ------------------------------------------------------------------------------
// <copyright file="manualQaCardContext.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * Recovers the catalog card a tester was on when they filed a bug.
 *
 * Triage asks the model one question the bug report alone cannot answer: is this a real defect,
 * or did the tester misread what the card told them to expect? That needs the card's own text,
 * and the catalogs live in this bundle rather than in Cosmos -- so the browser has to carry the
 * card to the triage endpoint.
 *
 * Lives outside the workspace component so the end-to-end contract fixture can call the same
 * function the Test Admin's browser calls, rather than restating the card by hand.
 */

import homePageV2Plan from '../data/manualQaHomePageV2TestPlan.json';
import iosViewPlan from '../data/manualQaIosViewTestPlan.json';
import iosBillingPlan from '../data/manualQaIosBillingTestPlan.json';
import qolsyncExploratoryPlan from '../data/manualQaQolsyncExploratoryTestPlan.json';
import qolsyncPhase2T1Plan from '../data/manualQaQolsyncPhase2T1TestPlan.json';
import qolsyncPhase2T2Plan from '../data/manualQaQolsyncPhase2T2TestPlan.json';

export const IOS_VIEW_PLAN_CATALOG_ID = 'iosclient-view-plan-v1';

/**
 * Every catalog a Test Admin can assign has to be listed here.
 *
 * The iOS view plan is assignable from Campaign Builder but was once missing, so bugs filed
 * against the default catalog reached triage carrying no card text at all -- silently, because
 * a missing card looks the same as a card with nothing to say.
 */
export const BUNDLED_MANUAL_QA_PLANS = [
  { catalogId: IOS_VIEW_PLAN_CATALOG_ID, plan: iosViewPlan },
  { catalogId: homePageV2Plan.catalogId, plan: homePageV2Plan },
  { catalogId: iosBillingPlan.catalogId, plan: iosBillingPlan },
  { catalogId: qolsyncExploratoryPlan.catalogId, plan: qolsyncExploratoryPlan },
  { catalogId: qolsyncPhase2T1Plan.catalogId, plan: qolsyncPhase2T1Plan },
  { catalogId: qolsyncPhase2T2Plan.catalogId, plan: qolsyncPhase2T2Plan },
];

/**
 * View id a bug was filed against.
 *
 * Bugs record `stepId` as `<viewId>-step-<n>`; `scenarioId` is the fallback for bugs filed
 * before that shape settled.
 *
 * @param {object} bug - Bug report document.
 * @returns {string} View id, or an empty string when it cannot be recovered.
 */
export function getManualQaViewId(bug) {
  if (!bug) return '';
  return String(bug.stepId || '').replace(/-step-\d+$/, '') || bug.scenarioId || '';
}

/**
 * Card the tester was on when they filed this bug.
 *
 * View ids are only guaranteed unique *within* a catalog, so the bug's own catalog is consulted
 * first: a bare scan across every bundled plan returns whichever plan happens to be listed
 * earliest, which is the wrong card whenever two catalogs share an id. The bug document carries
 * no catalogId of its own, so it comes from the run the bug belongs to.
 *
 * @param {object} bug - Bug report document.
 * @param {Array} runs - Persisted runs, used to resolve the bug's catalog.
 * @returns {{title: string, purpose: string, howToTest: string[]}|null} Card, or null.
 */
export function findManualQaCardContext(bug, runs = []) {
  const viewId = getManualQaViewId(bug);
  if (!viewId) return null;

  const run = (runs || []).find(candidate => candidate.id === bug.runId);
  const ordered = run?.catalogId
    ? [
      ...BUNDLED_MANUAL_QA_PLANS.filter(entry => entry.catalogId === run.catalogId),
      ...BUNDLED_MANUAL_QA_PLANS.filter(entry => entry.catalogId !== run.catalogId),
    ]
    : BUNDLED_MANUAL_QA_PLANS;

  for (const entry of ordered) {
    const view = (entry.plan?.views || []).find(candidate => candidate.id === viewId);
    if (view) {
      return {
        title: view.title || '',
        purpose: view.purpose || '',
        howToTest: view.howToTest || [],
      };
    }
  }

  return null;
}

const manualQaCardContext = {
  BUNDLED_MANUAL_QA_PLANS,
  IOS_VIEW_PLAN_CATALOG_ID,
  findManualQaCardContext,
  getManualQaViewId,
};

export default manualQaCardContext;
